import type {
  BaseItem,
  DecisionState,
  Handler,
  ObjectKey,
  TaskDecisionProvider,
  TaskState
} from '@atlaskit/task-decision';

export interface SandboxTaskDecisionResourceConfig {
  hasMore?: boolean;
  lag?: number;
  error?: boolean;
  empty?: boolean;
}

let debouncedTaskStateQuery: number | null = null;

export const objectKeyToString = (objectKey: ObjectKey) => {
  const { objectAri, localId } = objectKey;
  return `${objectAri}:${localId}`;
};

export const toggleTaskState = (state: string) =>
  state === 'DONE' ? 'TODO' : 'DONE';

export const findIndex = <T>(
  array: T[],
  predicate: (item: T) => boolean
): number => {
  let index = -1;
  array.some((item, i) => {
    if (predicate(item)) {
      index = i;
      return true;
    }
    return false;
  });

  return index;
};

export class SandboxTaskDecisionResource implements TaskDecisionProvider {
  private readonly config?: SandboxTaskDecisionResourceConfig;
  private subscribers: Map<string, Handler[]> = new Map();
  private cachedItems: Map<string, BaseItem<TaskState | DecisionState>> =
    new Map();
  private batchedKeys: Map<string, ObjectKey> = new Map();

  constructor(config?: SandboxTaskDecisionResourceConfig) {
    this.config = config;
    this.subscribers.clear();
    this.cachedItems.clear();
    this.batchedKeys.clear();
  }

  unsubscribeRecentUpdates() {
    return;
  }

  notifyRecentUpdates() {
    return;
  }

  getTaskState(): Promise<BaseItem<TaskState>[]> {
    return Promise.resolve([]);
  }

  toggleTask(objectKey: ObjectKey, state: TaskState): Promise<TaskState> {
    // Optimistically notify subscribers that the task have been updated so that they can re-render accordingly
    this.notifyUpdated(objectKey, state);

    return new Promise<TaskState>((resolve) => {
      const key = objectKeyToString(objectKey);
      const cached = this.cachedItems.get(key);
      if (cached) {
        cached.state = state;
        this.cachedItems.set(key, cached);
      } else {
        this.cachedItems.set(key, {
          ...objectKey,
          state
        } as BaseItem<DecisionState | TaskState>);
      }

      resolve(state);

      const lag = (this.config && this.config.lag) || 0;
      window.setTimeout(() => {
        if (this.config && this.config.error) {
          // Undo optimistic change
          this.notifyUpdated(objectKey, toggleTaskState(state));
        } else {
          this.notifyUpdated(objectKey, state);
        }
      }, 500 + lag);
    });
  }

  subscribe(objectKey: ObjectKey, handler: Handler) {
    const key = objectKeyToString(objectKey);
    const handlers = this.subscribers.get(key) || [];
    handlers.push(handler);
    this.subscribers.set(key, handlers);

    const cached = this.cachedItems.get(key);
    if (cached) {
      this.notifyUpdated(objectKey, cached.state);
      return;
    }

    if (debouncedTaskStateQuery) {
      clearTimeout(debouncedTaskStateQuery);
    }

    this.queueItem(objectKey);

    debouncedTaskStateQuery = window.setTimeout(() => {
      this.getTaskState().then((tasks) => {
        tasks.forEach((task) => {
          const { objectAri, localId } = task;
          const objectKey = { objectAri, localId };
          this.cachedItems.set(objectKeyToString(objectKey), task);

          this.dequeueItem(objectKey);
          this.notifyUpdated(objectKey, task.state);
        });
      });
    }, 1);
  }

  unsubscribe(objectKey: ObjectKey, handler: Handler) {
    const key = objectKeyToString(objectKey);
    const handlers = this.subscribers.get(key);
    if (!handlers) {
      return;
    }

    const index = findIndex(handlers, (h) => h === handler);

    if (index !== -1) {
      handlers.splice(index, 1);
    }

    if (handlers.length === 0) {
      this.subscribers.delete(key);
    } else {
      this.subscribers.set(key, handlers);
    }
  }

  notifyUpdated(objectKey: ObjectKey, state: TaskState | DecisionState) {
    const key = objectKeyToString(objectKey);
    const handlers = this.subscribers.get(key);
    if (!handlers) {
      return;
    }

    handlers.forEach((handler) => {
      handler(state);
    });
  }

  private queueItem(objectKey: ObjectKey) {
    const key = objectKeyToString(objectKey);
    if (this.batchedKeys.get(key)) {
      return;
    }

    this.batchedKeys.set(key, objectKey);
  }

  private dequeueItem(objectKey: ObjectKey) {
    const key = objectKeyToString(objectKey);
    this.batchedKeys.delete(key);
  }
}
